import React, { useEffect, useRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useCreateProjectMutation, useGetProjectsByCompanyQuery } from "gql/generated/types";
import { GET_PROJECTS_BY_COMPANY } from "gql/project.operations";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Controller, ControllerRenderProps, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { getFormErrorMessage } from "../utils/utils";

type ProjectDropDownProps = {
  companyRef: string;
};

type ProjectFormValues = {
  title: string;
};

const schema = z.object({
  title: z.string().min(1, { message: "Required" }),
});

const ProjectDropDown: React.FC<ProjectDropDownProps> = ({
  companyRef,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(schema),
  });
  const { projectId } = useParams();
  const navigate = useNavigate();
  const op = useRef<OverlayPanel>(null);
  const { data } = useGetProjectsByCompanyQuery({
    variables: { refName: companyRef },
  });

  useEffect(() => {
    if (!projectId && data?.mdmProjectsByCompany && data?.mdmProjectsByCompany.length > 0) {
      navigate(`/${data?.mdmProjectsByCompany[0]?.id}/`);
    }
  }, [projectId,data, navigate]);

  const [createProjectMutation] = useCreateProjectMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Project created successfully",
        life: 1000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
  });

  const handleCreateProject: SubmitHandler<ProjectFormValues> = projectData => {
    createProjectMutation({
      variables: {
        project: { companyReference: companyRef, title: projectData.title },
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_PROJECTS_BY_COMPANY, variables: {refName: companyRef}}, (oldData) => ({ 
          mdmProjectsByCompany: [...oldData.mdmProjectsByCompany, qData?.createMdmProject]
        }) );
      }
    });
    reset();
    op.current?.hide();
  };

  const handleChange = (e: DropdownChangeParams ) => {
    navigate(`/${e.value}`)
  }

  const renderTitleControl = (field: ControllerRenderProps<ProjectFormValues, any>) => (
    <InputLabelWrapper labelName="Project name" className="w-full">
      <div className="p-inputgroup">
        <InputText
          value={field.value}
          placeholder="Project name"
          onChange={e => field.onChange(e.target.value)}
        />
        <Button label="Create" className="p-button-success" type="submit" />
      </div>
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );

  return (
    <div className="p-inputgroup w-15rem">
      <Dropdown
        value={projectId ?? data?.mdmProjectsByCompany[0]?.id}
        options={data?.mdmProjectsByCompany || []}
        optionLabel="title"
        optionValue="id"
        onChange={handleChange}
        placeholder="Select project"
        className="w-13rem"
      />
      <Button
        icon="pi pi-plus"
        title="Create Project"
        onClick={e => op.current?.toggle(e)}
        className="p-inputgroup-addon p-button p-button-success p-button-text bg-gray-200 border-gray-300"
      />
      <OverlayPanel ref={op}>
        <form onSubmit={handleSubmit(handleCreateProject)}>
          <Controller
            defaultValue=""
            name="title"
            control={control}
            render={({ field }) => renderTitleControl(field)}
          />
        </form>
      </OverlayPanel>
    </div>
  );
};

export default ProjectDropDown;
